import React, { useEffect, useState } from "react";
import { TrainingCompliance } from "../../types/training";
import EmployeeTrainingTable from "./EmployeeTrainingTable";
import { Search, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

interface Props {
  complianceData: TrainingCompliance[];
}

const PIE_COLORS = ["#34D399", "#EF4444"]; // Green: compliant, Red: non-compliant

type View = 'table' | 'chart';

interface TableProps {
  complianceData: TrainingCompliance[];
  onSelect: (training: TrainingCompliance) => void;
}

function TrainingComplianceTable({ complianceData, onSelect }: TableProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [sortConfig, setSortConfig] = useState<{
    key: 'trainingName' | 'trainingClient' | null;
    direction: 'asc' | 'desc';
  }>({
    key: null,
    direction: 'asc'
  });

  const [filteredData, setFilteredData] = useState(complianceData.filter(item => {
    if (clientFilter !== "all" && item.trainingClient !== clientFilter) return false;
    const term = searchTerm.toLowerCase();
    return (
      item.trainingName.toLowerCase().includes(term) ||
      (item.trainingBadge || "").toLowerCase().includes(term) ||
      (item.trainingClient || "").toLowerCase().includes(term)
    );
  }));

  const handleSort = (key: 'trainingName' | 'trainingClient') => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedData = [...filteredData].sort((a, b) => {
      const aValue = a[key] || '';
      const bValue = b[key] || '';
      if (direction === 'asc') {
        return aValue.localeCompare(bValue);
      }
      return bValue.localeCompare(aValue);
    });
    setFilteredData(sortedData);
  };

  const getSortIcon = (key: 'trainingName' | 'trainingClient') => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? 
      <ArrowUp className="tw-w-4 tw-h-4 tw-inline-block tw-ml-1" /> : 
      <ArrowDown className="tw-w-4 tw-h-4 tw-inline-block tw-ml-1" />;
  };

  useEffect(() => {
    console.log(clientFilter);
    switch (clientFilter) {
      case "all":
        setFilteredData(complianceData);
        break;
      case "Direct Support":
        setFilteredData(complianceData.filter(item => item.directSupportServices));
        break;
      case "Remote Support":
        setFilteredData(complianceData.filter(item => item.remoteSupportServices));
        break;
      case "Hybrid":
        setFilteredData(complianceData.filter(item => item.directSupportServices && item.remoteSupportServices));
        break;
    }
  }, [clientFilter]);

  return (
    <div className="tw-overflow-x-auto">
      <div className="tw-flex tw-items-center tw-gap-4 tw-mb-4">
        <div>
          <select
            value={clientFilter}
            onChange={e => setClientFilter(e.target.value)}
            className="tw-form-select tw-border-gray-300 tw-rounded tw-px-2 tw-py-1 tw-text-sm"
          >
            <option value="all">All</option>
            <option value="Direct Support">Direct Support</option>
            <option value="Remote Support">Remote Support</option>
            <option value="Hybrid">Hybrid</option>
          </select>
        </div>
        <div className="tw-relative tw-flex-1">
          <span className="tw-absolute tw-left-2 tw-top-1.5 tw-text-gray-400">
            <Search className="tw-w-4 tw-h-4" />
          </span>
          <input
            type="text"
            placeholder="Search trainings"
            className="tw-w-full tw-pl-8 tw-pr-3 tw-py-1 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <table className="tw-w-full tw-text-sm tw-text-left">
        <thead>
          <tr className="tw-bg-gray-50">
            <th 
              className="tw-px-4 tw-py-2 tw-cursor-pointer hover:tw-bg-gray-100"
              onClick={() => handleSort('trainingName')}
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                Training Name
                {getSortIcon('trainingName')}
              </div>
            </th>
            <th 
              className="tw-px-4 tw-py-2 tw-cursor-pointer hover:tw-bg-gray-100"
              onClick={() => handleSort('trainingClient')}
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                Client
                {getSortIcon('trainingClient')}
              </div>
            </th>
            <th className="tw-px-4 tw-py-2">Compliant Count</th>
            <th className="tw-px-4 tw-py-2">Non-Compliant Count</th>
            <th className="tw-px-4 tw-py-2 text-center">Compliance Ratio</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map(item => {
            const pieData = [
              { name: "Compliant", value: item.totalComplianceCount },
              { name: "Non-Compliant", value: item.totalNotComplianceCount },
            ];
            return (
              <tr
                key={item.trainingComplianceID}
                className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50 tw-cursor-pointer"
                onClick={() => onSelect(item)}
              >
                <td className="tw-px-4 tw-py-2">
                  <div className="tw-flex tw-flex-col">
                    <span className="tw-font-medium tw-text-gray-900">{item.trainingName}</span>
                    <div className="tw-flex tw-flex-row tw-gap-2">
                    {item.trainingBadge && (
                      <span className="tw-bg-cyan-100 tw-text-cyan-700 tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-semibold tw-self-start">
                        {item.trainingBadge}
                      </span>
                    )}
                    {item.expiryDate && new Date(item.expiryDate) < new Date() && (
                      <span className="tw-bg-red-100 tw-text-red-700 tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-semibold tw-self-start">
                        Expired
                      </span>
                    )}
                    {item.expiryDate && new Date(item.expiryDate) <= new Date(new Date().setDate(new Date().getDate() + 30)) && new Date(item.expiryDate) > new Date() && (
                      <span className="tw-bg-yellow-100 tw-text-yellow-700 tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-semibold tw-self-start">
                        Expires in {Math.ceil((new Date(item.expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days
                      </span>
                    )}
                    </div>
                    
                  </div>
                </td>
                <td className="tw-px-4 tw-py-2">{item.trainingClient || "—"}</td>
                <td className="tw-px-4 tw-py-2">{item.totalComplianceCount}</td>
                <td className="tw-px-4 tw-py-2">{item.totalNotComplianceCount}</td>
                <td className="tw-px-4 tw-py-2 tw-flex tw-justify-center">
                  <ResponsiveContainer width={40} height={40}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={16}
                        innerRadius={8}
                        paddingAngle={2}
                      >
                        {pieData.map((_, idx) => (
                          <Cell key={`cell-${idx}`} fill={PIE_COLORS[idx]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TrainingComplianceChart({ complianceData }: Props) {
  const chartData = complianceData
    .map((item) => {
      const total = item.totalComplianceCount + item.totalNotComplianceCount;
      const compliantRate = total > 0 ? (item.totalComplianceCount / total) * 100 : 0;
      const nonCompliantRate = total > 0 ? (item.totalNotComplianceCount / total) * 100 : 0;
      return {
        trainingName: item.trainingName,
        compliantRate: parseFloat(compliantRate.toFixed(1)),
        nonCompliantRate: parseFloat(nonCompliantRate.toFixed(1)),
      };
    })
    .sort((a, b) => b.nonCompliantRate - a.nonCompliantRate);

  const chartHeight = Math.max(200, chartData.length * 40);

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart
        layout="vertical"
        data={chartData}
        margin={{ top: 20, right: 30, left: 150, bottom: 20 }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis type="number" domain={[0, 100]} tickFormatter={val => `${val}%`} />
        <YAxis type="category" dataKey="trainingName" width={150} tick={{ fontSize: 12 }} />
        <Tooltip
          formatter={(value: number, name: string) => [
            `${value}%`, name === 'compliantRate' ? 'Compliant' : 'Non-Compliant'
          ]}
          wrapperStyle={{ fontSize: 12 }}
        />
        <Bar dataKey="compliantRate" name="Compliant" barSize={10} fill="#34D399" />
        <Bar dataKey="nonCompliantRate" name="Non-Compliant" barSize={10} fill="#EF4444" />
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function TrainingComplianceDashboard({ complianceData }: Props) {
  const [view, setView] = useState<View>('chart');
  const [selectedTraining, setSelectedTraining] = useState<TrainingCompliance | null>(null);

  return (
    <div className="tw-bg-white tw-rounded-2xl tw-shadow tw-p-6 tw-w-full">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <h2 className="tw-text-lg tw-font-semibold">
          {selectedTraining ? (
            <div className="tw-flex tw-items-center tw-space-x-2">
              <button 
                onClick={() => setSelectedTraining(null)}
                className="tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors"
              >
                All Trainings
              </button>
              <ChevronRight className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span>{selectedTraining.trainingName}</span>
            </div>
          ) : (
            'Training Compliance Overview'
          )}
        </h2>
        <div className="tw-flex tw-space-x-2">
          {selectedTraining ? (<></>
            // <button
            //   onClick={() => setSelectedTraining(null)}
            //   className="tw-px-4 tw-py-2 tw-rounded tw-bg-gray-200 hover:tw-bg-gray-300 tw-transition-colors"
            // >
            //   Back to all trainings
            // </button>
          ) : (
            <>
              <button
                onClick={() => setView('chart')}
                className={`tw-px-4 tw-py-2 tw-rounded ${
                  view === 'chart' ? 'tw-bg-cyan-600 tw-text-white' : 'tw-bg-gray-200'
                }`}
              >
                Chart
              </button>
              <button
                onClick={() => setView('table')}
                className={`tw-px-4 tw-py-2 tw-rounded ${
                  view === 'table' ? 'tw-bg-cyan-600 tw-text-white' : 'tw-bg-gray-200'
                }`}
              >
                Table
              </button>
            </>
          )}
        </div>
      </div>
      {selectedTraining ? (
        <EmployeeTrainingTable complianceData={selectedTraining.complianceEntries} />
      ) : view === 'table' ? (
        <TrainingComplianceTable
          complianceData={complianceData}
          onSelect={setSelectedTraining}
        />
      ) : (
        <TrainingComplianceChart complianceData={complianceData} />
      )}
    </div>
  );
}
